import { gql, useMutation } from '@apollo/client'
import { Browser } from '@capacitor/browser'
import { useEffect, useState } from 'react'

import { SaveOauthToken } from '@/clients/utoniq-core/schema'
import { useAuth, useMessage, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'

import { State } from './template'

export type Action = {
  connectTwitter: () => Promise<void>
  disConnectTwitter: () => Promise<void>
  options?: {
    messages?: {
      connected?: string
    }
  }
}
export type Option = {
  messages?: {
    connected?: string
  }
}

export const useViewModel = makeViewModel<State, Action>(() => {
  const {
    loading,
    connected,
    displayName,
    connectTwitter,
    disConnectTwitter,
    isBoundWithTwitter,
  } = useTwitterConnection()

  return {
    state: {
      integrations: {
        twitter: {
          loading,
          connected,
          displayName,
          isBound: isBoundWithTwitter,
        },
      },
    },
    actions: { connectTwitter, disConnectTwitter },
  }
})

/**
 * @description
 * twitterとの連携状態・連携メソッド・連携解除メソッドを呼び出すhook
 *
 * @returns loading ローディング状態
 * @returns connected 連携状態
 * @returns displayName snsアカウント名
 * @returns onConnectTwitter twitter連携メソッド
 * @returns onDisConnectTwitter twitter連携解除メソッド
 * @returns isBoundWithTwitter twitterのみで登録しているユーザーかどうか
 */
export const useTwitterConnection = (opt?: Option) => {
  const { t } = useTranslation('integrations')
  const { showError, showMessage } = useMessage()

  const [loading, setLoading] = useState(false)
  const [connected, setConnected] = useState(false)
  const [displayName, setDisplayName] = useState('')

  const { user, linkTwitter, unLinkTwitter, reloadUser, isBoundWithSNS } =
    useAuth()

  const [saveTwitterToken] = useMutation(SaveOauthToken)
  const [removeTwitterToken] = useMutation(REMOVE_OAUTH_TOKEN)

  // twitterで新規登録したユーザーかどうかの確認
  const isBoundWithTwitter =
    isBoundWithSNS && user?.providers[0].providerId === 'twitter.com'

  /**
   * 既にtwitterアカウントと連携済みかどうか確認
   */
  useEffect(() => {
    if (!user) return

    user.providers?.forEach(({ providerId, displayName }) => {
      if (providerId !== 'twitter.com') return

      if (displayName) setDisplayName(displayName)

      setConnected(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    reloadWhenFinishBrowser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * アプリ内部ブラウザから戻ってきた時にユーザーの状態を更新する
   */
  const reloadWhenFinishBrowser = () => {
    // On mobile, the popup will open in the web browser,
    // so this event is only handled when the popup closes,
    // but not when the browser is closed.
    // Therefore, I will set a 5-second delay to call the user again.
    const DELAY = getPlatform() === 'android' ? 5000 : 0
    Browser.addListener('browserFinished', () => {
      setTimeout(() => {
        reloadUser().catch(showError)
      }, DELAY)
    })
  }

  const connectTwitter = async () => {
    setLoading(true)
    await linkTwitter()
      .then(res => {
        if (!res) return
        const { credential, providerId } = res
        return saveTwitterToken({
          variables: {
            inputs: {
              providerId,
              oauthToken: credential?.oauthToken,
              oauthTokenSecret: credential?.oauthTokenSecret,
            },
          },
        })
      })
      .then(() => {
        setConnected(true)
        showMessage(opt?.messages?.connected ?? t('connectedTwitter'))
      })
      .catch(e => {
        showError(e)
      })
      .finally(() => setLoading(false))
  }

  const disConnectTwitter = async () => {
    setLoading(true)
    unLinkTwitter()
      .then(() =>
        removeTwitterToken({
          variables: {
            providerId: 'twitter',
          },
        })
      )
      .then(() => {
        showMessage(t('disConnectTwitter'))
        setConnected(false)
        setDisplayName('')
        reloadUser()
      })
      .catch(e => showError(e))
      .finally(() => setLoading(false))
  }

  return {
    loading,
    connected,
    displayName,
    connectTwitter,
    disConnectTwitter,
    isBoundWithTwitter,
  }
}

const REMOVE_OAUTH_TOKEN = gql`
  mutation RemoveOauthToken($providerId: ProviderType!) {
    removeOauthToken(providerId: $providerId) {
      ... on User {
        id
      }
      ... on Artist {
        id
      }
    }
  }
`
